import React, { useState } from 'react';
import { IntervalKey, PeriodKey, IntervalSection } from '../../types';
import { INTRADAY_INTERVALS, EXPANDABLE_INTERVALS } from '../../constants';
import { ReactComponent as ArrowDownIcon } from '../../../../assets/icons/arrow-down-s-line.svg';
import SettingsPopup from '../common/SettingsPopup';
import SettingsCheckbox from '../common/SettingsCheckbox';
import '../../../../Components/Common/typography.css';
import './TimePeriodSettings.css';

interface TimePeriodSettingsProps {
  selectedInterval: IntervalKey;
  selectedPeriod: PeriodKey;
  onIntervalChange: (interval: IntervalKey) => void;
  onPeriodChange: (period: PeriodKey) => void;
}

const TimePeriodSettings: React.FC<TimePeriodSettingsProps> = ({
  selectedInterval,
  selectedPeriod,
  onIntervalChange,
  onPeriodChange,
}) => {
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({
    d: selectedInterval === 'd',
    w: selectedInterval === 'w',
    m: selectedInterval === 'm',
  });

  const toggleSection = (key: string) => {
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleIntradaySelect = (key: IntervalKey) => {
    onIntervalChange(key);
  };

  const handleExpandableSelect = (section: IntervalSection) => {
    onIntervalChange(section.key);
    if (!expandedSections[section.key]) {
      setExpandedSections((prev) => ({ ...prev, [section.key]: true }));
    }
  };

  const handlePeriodSelect = (period: PeriodKey) => {
    onPeriodChange(period);
  };

  return (
    <SettingsPopup className="time-period-popup">
      {/* Intraday intervals */}
      {INTRADAY_INTERVALS.map(({ key, label }) => (
        <div
          key={key}
          className="time-period-row"
          onClick={() => handleIntradaySelect(key)}
        >
          <SettingsCheckbox checked={selectedInterval === key} onChange={() => handleIntradaySelect(key)} />
          <span className="body-m-regular">{label}</span>
        </div>
      ))}

      {/* Expandable intervals (Daily, Weekly, Monthly) */}
      {EXPANDABLE_INTERVALS.map((section) => {
        const isSelected = selectedInterval === section.key;
        const isExpanded = expandedSections[section.key];

        return (
          <div key={section.key} className="time-period-section">
            <div
              className="time-period-row time-period-row-expandable"
              onClick={() => handleExpandableSelect(section)}
            >
              <SettingsCheckbox checked={isSelected} onChange={() => handleExpandableSelect(section)} />
              <span className="body-m-regular">{section.label}</span>
              <span
                className={`time-period-arrow${isExpanded ? ' time-period-arrow-up' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleSection(section.key);
                }}
              >
                <ArrowDownIcon />
              </span>
            </div>

            {isExpanded && (
              <div className="time-period-buttons">
                {section.periods.map((period) => (
                  <button
                    key={period}
                    className={`time-period-button body-s-regular${
                      isSelected && selectedPeriod === period
                        ? ' time-period-button-active'
                        : ''
                    }`}
                    onClick={() => {
                      if (!isSelected) {
                        onIntervalChange(section.key);
                      }
                      handlePeriodSelect(period);
                    }}
                  >
                    {period}
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </SettingsPopup>
  );
};

export default TimePeriodSettings;
